import React, { useState } from 'react';
import usePostRequest from '../Hooks/usePostRequest';
import {
    Alert,
    Avatar,
    Box,
    Button,
    Card,
    CardContent,
    CardMedia,
    Chip,
    CircularProgress,
    Container,
    FormControl,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    TextField,
    Typography
} from '@mui/material';
import { brandGradient } from '../shared-theme/themePrimitives';
import { SENTIMENT_COLORS } from '../utils/sentiment';

const PLATFORMS = ['TikTok', 'Instagram', 'YouTube', 'Facebook'];

const loadingMessages = [
    'Searching our creators',
    'Finding your best-fit matches…'
];

const compact = new Intl.NumberFormat('en', { notation: 'compact', maximumFractionDigits: 1 });

// 1234 -> "1.2K"; a number the platform does not provide -> an em dash
export const formatNumber = (value) =>
    value === null || value === undefined || Number.isNaN(Number(value)) ? '—' : compact.format(value);

// Links come from scraped data: only http(s) may become clickable (never javascript: and friends)
export const safeUrl = (url) => (typeof url === 'string' && /^https?:\/\//i.test(url) ? url : undefined);

const gradientText = {
    background: brandGradient,
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent'
};

const Stat = ({ label, children }) => (
    <Box>
        <Typography variant="subtitle2" color="secondary.main" fontWeight="bold">{label}</Typography>
        <Typography variant="body1" component="div">{children}</Typography>
    </Box>
);

const LiveSearchPage = () => {
    const { post, data, loading, error } = usePostRequest();

    const [platform, setPlatform] = useState('TikTok');
    const [category, setCategory] = useState('');
    const [loadingMessage, setLoadingMessage] = useState('');
    const [submitted, setSubmitted] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!category.trim()) return;
        setSubmitted(true);
        setLoadingMessage(loadingMessages[Math.floor(Math.random() * loadingMessages.length)]);
        await post('/live-search', {
            platforms: [platform],
            categories: [category.trim()]
        });
    };

    const results = Array.isArray(data?.results) ? data.results : [];
    const summary = data?.summary || {};
    const warnings = Array.isArray(data?.warnings) ? data.warnings : [];
    const showResults = !loading && !error && submitted && data && Array.isArray(data.results);

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Typography variant="h4" gutterBottom sx={{ ...gradientText, mb: 3, textAlign: 'center', fontWeight: 700 }}>
                Creator Search
            </Typography>

            {/* Search form */}
            <Paper
                variant="outlined"
                component="form"
                onSubmit={handleSubmit}
                sx={{
                    mb: 5,
                    p: 2.5,
                    borderRadius: 3,
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: 2,
                    justifyContent: 'center',
                }}
            >
                <FormControl sx={{ width: { xs: '100%', sm: 160 } }}>
                    <InputLabel id="platform-label">Platform</InputLabel>
                    <Select
                        labelId="platform-label"
                        label="Platform"
                        value={platform}
                        onChange={(e) => setPlatform(e.target.value)}
                    >
                        {PLATFORMS.map((name) => <MenuItem key={name} value={name}>{name}</MenuItem>)}
                    </Select>
                </FormControl>
                <TextField
                    label="Category"
                    variant="outlined"
                    sx={{ width: { xs: '100%', sm: '50%' } }}
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    placeholder="e.g. Music"
                />
                <Button
                    type="submit"
                    variant="contained"
                    disabled={loading}
                    sx={{ borderRadius: 2, px: 4 }}
                >
                    Search
                </Button>
            </Paper>

            {/* Loading */}
            {loading && (
                <Box textAlign="center" mt={5}>
                    <CircularProgress size={50} />
                    <Typography variant="h6" mt={2}>{loadingMessage}</Typography>
                    <Typography variant="body2" color="textSecondary">Searches our own creators — this is instant.</Typography>
                </Box>
            )}

            {/* Error: show the real reason, not a generic message */}
            {error && !loading && (
                <Box textAlign="center" mt={5}>
                    <Alert severity="error" sx={{ display: 'inline-flex' }}>
                        {error.error || error.message || 'Something went wrong'}
                    </Alert>
                </Box>
            )}

            {/* Notes from the search (e.g. why nothing was found, or that sentiment is unavailable) */}
            {showResults && warnings.length > 0 && (
                <Stack spacing={1} mb={3}>
                    {warnings.map((warning, index) => (
                        <Alert key={index} severity="warning">{warning}</Alert>
                    ))}
                </Stack>
            )}

            {/* Summary of the result set */}
            {showResults && results.length > 0 && (
                <Box
                    data-testid="summary"
                    sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', justifyContent: 'center', mb: 3 }}
                >
                    <Chip label={`${summary.count ?? results.length} of ${summary.candidates ?? '?'} posts`} />
                    <Chip label={`Avg followers ${formatNumber(summary.avg_followers)}`} />
                    <Chip label={`Avg engagement ${summary.avg_engagement_rate != null ? `${summary.avg_engagement_rate}%` : '—'}`} />
                    {Object.entries(summary.sentiment || {}).filter(([, count]) => count > 0).map(([label, count]) => (
                        <Chip key={label} color={SENTIMENT_COLORS[label] || 'default'} variant="outlined"
                              label={`${label.charAt(0).toUpperCase()}${label.slice(1)}: ${count}`} />
                    ))}
                </Box>
            )}

            {/* Results */}
            {showResults && results.length > 0 && (
                <Stack spacing={3}>
                    {results.map((item, index) => (
                        <Card
                            key={item.post_url || index}
                            variant="outlined"
                            sx={{
                                display: 'flex',
                                flexDirection: { xs: 'column', sm: 'row' },
                                gap: 3,
                                p: 3,
                                borderRadius: 4,
                                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                                '&:hover': {
                                    transform: 'translateY(-3px)',
                                    boxShadow: 4,
                                }
                            }}
                        >
                            {safeUrl(item.thumbnail) && (
                                <CardMedia
                                    component="img"
                                    image={safeUrl(item.thumbnail)}
                                    alt="Thumbnail"
                                    sx={{
                                        width: { xs: '100%', sm: 180 },
                                        height: 180,
                                        borderRadius: 3,
                                        objectFit: 'cover',
                                        border: '1px solid',
                                        borderColor: 'divider',
                                    }}
                                />
                            )}

                            <CardContent sx={{ flex: 1, p: 0 }}>
                                <Typography variant="caption" color="text.secondary">#{item.rank ?? index + 1}</Typography>
                                <Typography variant="h6" fontWeight="bold" sx={{ ...gradientText, display: 'block', mb: 1 }}>
                                    {item.caption || 'View post'}
                                </Typography>

                                {item.tags && (
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                        {item.tags}
                                    </Typography>
                                )}

                                <Box sx={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: 2,
                                    mb: 2,
                                    '& > div': { p: 1.5, borderRadius: 2, bgcolor: 'action.hover' }
                                }}>
                                    <Stat label="Views">{formatNumber(item.views)}</Stat>
                                    <Stat label="Likes">{formatNumber(item.likes)}</Stat>
                                    <Stat label="Comments">{formatNumber(item.comments)}</Stat>
                                    <Stat label="Engagement">
                                        {item.engagement_rate != null ? `${item.engagement_rate}%` : '—'}
                                    </Stat>
                                    <Stat label="Sentiment">
                                        <Chip
                                            size="small"
                                            color={SENTIMENT_COLORS[item.sentiment] || 'default'}
                                            label={item.sentiment && item.sentiment !== 'unknown' ? item.sentiment : 'n/a'}
                                            sx={{ textTransform: 'capitalize' }}
                                        />
                                    </Stat>
                                </Box>

                                {safeUrl(item.post_url) && (
                                    <Button
                                        variant="outlined"
                                        color="secondary"
                                        href={safeUrl(item.post_url)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        View Post
                                    </Button>
                                )}
                            </CardContent>

                            <Box sx={{
                                minWidth: 140,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: 1.5,
                                p: 2,
                                borderRadius: 3,
                                bgcolor: 'action.hover',
                            }}>
                                <Avatar
                                    src={safeUrl(item.profile_img)}
                                    alt={item.display_name || item.profile_name}
                                    sx={{ width: 80, height: 80, border: '2px solid', borderColor: 'secondary.main', mb: 1 }}
                                >
                                    {(item.display_name || item.profile_name || '?')[0]?.toUpperCase()}
                                </Avatar>

                                <Typography variant="subtitle1" fontWeight="bold" textAlign="center" color="secondary.main">
                                    {item.display_name || item.profile_name}
                                </Typography>
                                {item.verified && <Chip size="small" color="info" label="Verified" />}

                                <Typography variant="caption" display="block" textAlign="center">
                                    <Box component="span" fontWeight="bold" color="secondary.main">
                                        {formatNumber(item.followers)}
                                    </Box> {item.platform === 'youtube' ? 'subscribers' : 'followers'}
                                </Typography>

                                {safeUrl(item.profile_url) && (
                                    <Button
                                        variant="text"
                                        color="secondary"
                                        href={safeUrl(item.profile_url)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        size="small"
                                        sx={{ fontWeight: 'bold' }}
                                    >
                                        View Profile
                                    </Button>
                                )}
                            </Box>
                        </Card>
                    ))}
                </Stack>
            )}

            {/* No results */}
            {showResults && results.length === 0 && (
                <Typography mt={4} textAlign="center">No results found for this category.</Typography>
            )}
        </Container>
    );
};

export default LiveSearchPage;
